/**
 * Slider hooks — data fetching and mutations for homepage promotional banners.
 *
 * This file exports two APIs:
 *
 * 1. **Legacy hook** (`useSliders`) — manual fetch/dispatch pattern used by existing
 *    components. Kept for backward compatibility; new code should prefer the TanStack
 *    Query hooks below.
 *
 * 2. **TanStack Query hooks** — `useGetSliders`, `useCreateSlider`, `useEditSlider`,
 *    `useDeleteSlider`, `useToggleSliderHidden`. These provide automatic caching,
 *    deduplication, and cache invalidation. They follow the same pattern established
 *    in `useVendors.js` (vendorKeys / useGetVendors / useUpdateVendor).
 *
 * The TanStack hooks sync data into Redux via `setSliders` so that components reading
 * from `state.slider.sliders` continue to work during the migration.
 */
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    createSlider,
    getSliders,
    removeSlider,
    updateSlider,
    toggleIsHidden,
    saveSliderOrder,
} from "services/sliders";
import { setSliders } from "store/sliderSlice";

// ---------------------------------------------------------------------------
// Legacy hook (manual fetch/dispatch) — existing components depend on this
// ---------------------------------------------------------------------------

const useSliders = () => {
    const sliders = useSelector((state) => state.slider.sliders);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const dispatch = useDispatch();

    const fetchSliders = async () => {
        setLoading(true);
        try {
            const data = await getSliders();
            dispatch(setSliders(data.results));
            return data;
        } catch (err) {
            setError(err.message || "Failed to fetch sliders");
        } finally {
            setLoading(false);
        }
    };

    const addSlider = async (newSlider) => {
        setLoading(true);
        try {
            const response = await createSlider(newSlider);
            fetchSliders();
            return response;
        } catch (err) {
            setError(err.response.data.message || "Failed to create slider");
            return err;
        } finally {
            setLoading(false);
        }
    };

    const editSlider = async (updatedData) => {
        setLoading(true);
        try {
            const updated = await updateSlider(updatedData);

            fetchSliders();

            return updated;
        } catch (err) {
            setError(err.message || "Failed to update slider");
        } finally {
            setLoading(false);
        }
    };

    const deleteSlider = async (id) => {
        setLoading(true);
        try {
            await removeSlider(id);
            fetchSliders();
            return true;
        } catch (err) {
            setError(err.message || "Failed to delete slider");
        } finally {
            setLoading(false);
        }
    };

    const toggleSliderHidden = async (id) => {
        setLoading(true);
        try {
            await toggleIsHidden(id);
            fetchSliders();
            return true;
        } catch (err) {
            setError(err.message || "Failed to toggle slider visibility");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Saves a new order of the sliders.
     *
     * The table moves AT ONCE, and the page-wide spinner is not used: an admin
     * presses the arrows several times in a row, and a spinner that replaced
     * the table after each press would make that impossible. When the save
     * fails, the list is loaded again, so the table shows the real order.
     *
     * @param {object[]} ordered - every slider, in the new order
     * @returns {Promise<{ok: true} | {ok: false, message: string|null}>}
     */
    const reorderSliders = async (ordered) => {
        dispatch(setSliders(ordered));
        try {
            await saveSliderOrder(ordered.map((slider) => slider.id));
            return { ok: true };
        } catch (err) {
            try {
                const data = await getSliders();
                dispatch(setSliders(data.results));
            } catch {
                // The list stays as it is; the message below still shows.
            }
            return { ok: false, message: err.response?.data?.message || null };
        }
    };

    return {
        sliders,
        loading,
        error,
        fetchSliders,
        addSlider,
        editSlider,
        deleteSlider,
        toggleSliderHidden,
        reorderSliders,
    };
};

export default useSliders;

// ---------------------------------------------------------------------------
// TanStack Query hooks (preferred for new code)
// ---------------------------------------------------------------------------

/** Query key factory — keeps cache keys consistent across all slider hooks. */
export const sliderKeys = {
    all: ["sliders"],
};

/**
 * Fetches all sliders via TanStack Query and syncs into Redux.
 *
 * Multiple components can call this hook — only ONE network request is made
 * (TanStack deduplicates concurrent requests with the same queryKey). Data is
 * cached for 5 minutes (default staleTime from queryClient.js).
 *
 * Redux sync ensures components still reading `state.slider.sliders` work
 * during migration to pure TanStack Query consumption.
 */
export const useGetSliders = () => {
    const dispatch = useDispatch();

    const query = useQuery({
        queryKey: sliderKeys.all,
        queryFn: async () => {
            const data = await getSliders();
            return data.results;
        },
    });

    // Sync query data into Redux for components that still read from the store
    useEffect(() => {
        if (query.data) {
            dispatch(setSliders(query.data));
        }
    }, [query.data, dispatch]);

    return {
        sliders: query.data ?? [],
        loading: query.isLoading,
        error: query.error?.message ?? null,
        refetch: query.refetch,
    };
};

/** Create a slider and invalidate the sliders cache so the list refreshes. */
export const useCreateSlider = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createSlider,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: sliderKeys.all });
        },
    });
};

/** Update a slider and invalidate the sliders cache. */
export const useEditSlider = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updateSlider,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: sliderKeys.all });
        },
    });
};

/** Delete a slider and invalidate the sliders cache. */
export const useDeleteSlider = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: removeSlider,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: sliderKeys.all });
        },
    });
};

/**
 * Toggle a slider's isHidden flag. Invalidates the slider cache on success so
 * the list immediately reflects the new visibility state.
 */
export const useToggleSliderHidden = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: toggleIsHidden,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: sliderKeys.all });
        },
    });
};
